import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { isGlobalError } from '@/lib/queryClient';
import { hasFleetCode } from '@/lib/fleet/errors';
import {
  addLink,
  getAccount,
  getAccounts,
  getDevice,
  getDevices,
  getGroup,
  getGroups,
  getHealth,
  getLedger,
  getSimDays,
  getSims,
  getSimsLive,
  getMiloAccess,
  getMiloConversation,
  getMiloConversations,
  getMiloCustomers,
  getMiloFailedTurn,
  getMiloInsights,
  getMiloSettings,
  getMiloUsage,
  getZones,
  removeLink,
  saveMiloSettings,
  setAccountDisabled,
  setAccountMilo,
  setDeviceAccumulators,
  setDeviceGroup,
  setDeviceSettings,
  setMiloCustomer,
  startRebuild,
  startSimSync,
  testMiloKey,
} from './api';
import type {
  AccountMiloInput,
  DateRange,
  DeviceAccumulatorsInput,
  DeviceSettingsInput,
  FleetResult,
  LedgerView,
  LinkInput,
  MiloConversationFilters,
  MiloCustomerInput,
  MiloProvider,
  MiloSettingsInput,
  RebuildInput,
} from './types';

/** "Polaris caches lists 30 s" (spec, Data flow). */
export const FLEET_STALE_MS = 30_000;

/** SIM data's Live toggle refreshes this often while the page is visible (SIM data spec, Polaris). */
export const SIM_LIVE_REFRESH_MS = 300_000;

/** "A running rebuild's status refreshes every 3 s" (spec, Data flow). */
export const REBUILD_POLL_MS = 3_000;

export const fleetKeys = {
  all: ['fleet'] as const,
  accounts: () => [...fleetKeys.all, 'accounts'] as const,
  account: (id: number) => [...fleetKeys.all, 'accounts', id] as const,
  devices: () => [...fleetKeys.all, 'devices'] as const,
  device: (id: number) => [...fleetKeys.all, 'devices', id] as const,
  groups: () => [...fleetKeys.all, 'groups'] as const,
  group: (id: number) => [...fleetKeys.all, 'groups', id] as const,
  zones: () => [...fleetKeys.all, 'zones'] as const,
  ledger: (range?: DateRange) => [...fleetKeys.all, 'ledger', range ?? null] as const,
  health: () => [...fleetKeys.all, 'health'] as const,
  sims: (range: DateRange, live: boolean) => [...fleetKeys.all, 'sims', range, live] as const,
  simDays: (iccid: string, range: DateRange) => [...fleetKeys.all, 'sims', 'days', iccid, range] as const,
  milo: () => [...fleetKeys.all, 'milo'] as const,
  miloSettings: () => [...fleetKeys.milo(), 'settings'] as const,
  miloAccess: () => [...fleetKeys.milo(), 'access'] as const,
  miloCustomers: () => [...fleetKeys.milo(), 'customers'] as const,
  miloUsage: (month?: string) => [...fleetKeys.milo(), 'usage', month ?? null] as const,
  miloConversations: (filters: MiloConversationFilters) => [...fleetKeys.milo(), 'conversations', filters] as const,
  miloConversation: (id: string) => [...fleetKeys.milo(), 'conversation', id] as const,
  miloFailedTurn: (id: string) => [...fleetKeys.milo(), 'failed', id] as const,
  miloInsights: (range: DateRange) => [...fleetKeys.milo(), 'insights', range] as const,
};

/**
 * Retry only what may pass on a second try.
 *
 * echo's own codes (`traccar_unavailable`, `milo_not_connected`, …) are its final word — it already
 * waited 10 s for Traccar — so they show at once instead of after the app-wide 503 back-off (whose
 * "Reconnecting…" notice would be wrong here: echo is up). echo itself unreachable keeps the
 * app-wide behaviour of riding out a deploy.
 */
export function fleetRetry(failureCount: number, error: unknown): boolean {
  if (isGlobalError(error) || hasFleetCode(error)) return false;
  return failureCount < 3;
}

/** Poll the ledger while a rebuild runs; stop as soon as it is done (or its status is unknown). */
export function ledgerRefetchInterval(result: FleetResult<LedgerView> | undefined): number | false {
  return result?.data.status?.running ? REBUILD_POLL_MS : false;
}

const reads = { staleTime: FLEET_STALE_MS, retry: fleetRetry } as const;

/* ── Reads ────────────────────────────────────────────────────────────── */

export const useFleetAccounts = () => useQuery({ queryKey: fleetKeys.accounts(), queryFn: getAccounts, ...reads });

export const useFleetAccount = (id: number) =>
  useQuery({ queryKey: fleetKeys.account(id), queryFn: () => getAccount(id), enabled: id > 0, ...reads });

export const useFleetDevices = () => useQuery({ queryKey: fleetKeys.devices(), queryFn: getDevices, ...reads });

export const useFleetDevice = (id: number) =>
  useQuery({ queryKey: fleetKeys.device(id), queryFn: () => getDevice(id), enabled: id > 0, ...reads });

export const useFleetGroups = () => useQuery({ queryKey: fleetKeys.groups(), queryFn: getGroups, ...reads });

export const useFleetGroup = (id: number) =>
  useQuery({ queryKey: fleetKeys.group(id), queryFn: () => getGroup(id), enabled: id > 0, ...reads });

export const useFleetZones = () => useQuery({ queryKey: fleetKeys.zones(), queryFn: getZones, ...reads });

export const ledgerQuery = (range?: DateRange) => ({
  queryKey: fleetKeys.ledger(range),
  queryFn: () => getLedger(range),
  ...reads,
  // A new range keeps the old grid on screen until the new one arrives.
  placeholderData: keepPreviousData,
  // Stop while the ledger is failing: the last good answer still says "running", but polling a failing echo
  // every 3 s helps nobody (the admin's retry or the next mount asks again).
  refetchInterval: (query: { state: { status: string; data: FleetResult<LedgerView> | undefined } }) =>
    query.state.status === 'error' ? false : ledgerRefetchInterval(query.state.data),
});

export const useFleetLedger = (range?: DateRange) => useQuery(ledgerQuery(range));

export const useFleetHealth = () => useQuery({ queryKey: fleetKeys.health(), queryFn: getHealth, ...reads });

/** SIM data: the stored view, or with `live` echo's live view (asks 1NCE) every 5 minutes while visible. */
export const simsQuery = (range: DateRange, live: boolean, enabled = true) => ({
  queryKey: fleetKeys.sims(range, live),
  queryFn: () => (live ? getSimsLive(range) : getSims(range)),
  enabled,
  ...reads,
  placeholderData: keepPreviousData,
  refetchInterval: live ? SIM_LIVE_REFRESH_MS : (false as const),
  refetchIntervalInBackground: false,
});

export const useFleetSims = (range: DateRange, live: boolean, enabled = true) =>
  useQuery(simsQuery(range, live, enabled));

export const useFleetSimDays = (iccid: string | null, range: DateRange) =>
  useQuery({
    queryKey: fleetKeys.simDays(iccid ?? '', range),
    queryFn: () => getSimDays(iccid ?? '', range),
    enabled: iccid !== null,
    ...reads,
  });

export const useMiloSettings = () =>
  useQuery({ queryKey: fleetKeys.miloSettings(), queryFn: getMiloSettings, ...reads });

export const useMiloAccess = () => useQuery({ queryKey: fleetKeys.miloAccess(), queryFn: getMiloAccess, ...reads });

export const useMiloCustomers = () =>
  useQuery({ queryKey: fleetKeys.miloCustomers(), queryFn: getMiloCustomers, ...reads });

export const useMiloUsage = (month?: string) =>
  useQuery({ queryKey: fleetKeys.miloUsage(month), queryFn: () => getMiloUsage(month), ...reads });

export const useMiloConversations = (filters: MiloConversationFilters) =>
  useInfiniteQuery({
    queryKey: fleetKeys.miloConversations(filters),
    queryFn: ({ pageParam }) => getMiloConversations(filters, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.data.nextCursor ?? undefined,
    ...reads,
  });

/**
 * echo audits every read of a conversation or a failed turn. So one open = one read: nothing is
 * refetched in the background while it is open (staleTime ∞, no focus refetch), and nothing is kept
 * after it closes (gcTime 0), so opening it again reads — and is audited — again.
 */
export const auditedReadOptions = {
  staleTime: Infinity,
  gcTime: 0,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

export const miloConversationQuery = (id: string) => ({
  queryKey: fleetKeys.miloConversation(id),
  queryFn: () => getMiloConversation(id),
  enabled: id.length > 0,
  ...reads,
  ...auditedReadOptions,
  // A retry would be a second read, so a second audit entry.
  retry: false,
});

export const miloFailedTurnQuery = (id: string) => ({
  queryKey: fleetKeys.miloFailedTurn(id),
  queryFn: () => getMiloFailedTurn(id),
  enabled: id.length > 0,
  ...reads,
  ...auditedReadOptions,
  retry: false,
});

export const useMiloConversation = (id: string) => useQuery(miloConversationQuery(id));

export const useMiloFailedTurn = (id: string) => useQuery(miloFailedTurnQuery(id));

export const useMiloInsights = (range: DateRange) =>
  useQuery({
    queryKey: fleetKeys.miloInsights(range),
    queryFn: () => getMiloInsights(range),
    ...reads,
    placeholderData: keepPreviousData,
  });

/* ── Writes ───────────────────────────────────────────────────────────── */

const AUDITED_PREFIXES = [fleetKeys.miloConversation('').slice(0, -1), fleetKeys.miloFailedTurn('').slice(0, -1)];

/**
 * True for the keys of reads echo audits (a conversation, a failed turn). Anything that refetches in
 * bulk must skip them: a refetch is another VIEW audit entry for nothing the admin did.
 */
export const isAuditedReadKey = (queryKey: readonly unknown[]): boolean =>
  AUDITED_PREFIXES.some((prefix) => prefix.every((part, index) => queryKey[index] === part));

/** Refetch everything fleet that is open, except the audited reads. */
export const invalidateFleet = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({
    queryKey: fleetKeys.all,
    predicate: (query) => !isAuditedReadKey(query.queryKey),
  });

/**
 * Every write is pessimistic: the button waits for echo, then everything fleet refetches. That
 * happens on failure too, so a "no longer exists" answer refreshes the list it came from.
 */
// eslint-disable-next-line no-unused-vars -- a function type's parameter name
const fleetWriteOptions = <TInput, TResult>(queryClient: QueryClient, write: (input: TInput) => Promise<TResult>) => ({
  mutationFn: write,
  onSettled: () => invalidateFleet(queryClient),
});

// eslint-disable-next-line no-unused-vars -- a function type's parameter name
function useFleetWrite<TInput, TResult>(write: (input: TInput) => Promise<TResult>) {
  return useMutation(fleetWriteOptions(useQueryClient(), write));
}

/**
 * The settings PUT carries a typed provider key in its variables, and `reset()` does not drop a
 * mutation from the MutationCache: `gcTime: 0` removes it as soon as it settles.
 */
export const saveMiloSettingsOptions = (queryClient: QueryClient) => ({
  ...fleetWriteOptions(queryClient, (input: MiloSettingsInput) => saveMiloSettings(input)),
  gcTime: 0,
});

export const useSetAccountDisabled = () =>
  useFleetWrite(({ id, disabled }: { id: number; disabled: boolean }) => setAccountDisabled(id, disabled));

export const useSetAccountMilo = () =>
  useFleetWrite(({ id, input }: { id: number; input: AccountMiloInput }) => setAccountMilo(id, input));

export const useAddLink = () => useFleetWrite((input: LinkInput) => addLink(input));

export const useRemoveLink = () => useFleetWrite((input: LinkInput) => removeLink(input));

export const useSetDeviceSettings = () =>
  useFleetWrite(({ id, input }: { id: number; input: DeviceSettingsInput }) => setDeviceSettings(id, input));

export const useSetDeviceAccumulators = () =>
  useFleetWrite(({ id, input }: { id: number; input: DeviceAccumulatorsInput }) => setDeviceAccumulators(id, input));

export const useSetDeviceGroup = () =>
  useFleetWrite(({ id, groupId }: { id: number; groupId: number | null }) => setDeviceGroup(id, groupId));

export const useStartRebuild = () => useFleetWrite((input: RebuildInput) => startRebuild(input));

export const useStartSimSync = () => useFleetWrite(() => startSimSync());

export const useSaveMiloSettings = () => useMutation(saveMiloSettingsOptions(useQueryClient()));

/**
 * A customer's model and budget. Like every fleet write it refetches everything fleet once it
 * settles — the Access tab's two lists (accounts and customers) included, so the header and the
 * logins under it show what Milo now holds.
 */
export const setMiloCustomerOptions = (queryClient: QueryClient) =>
  fleetWriteOptions(queryClient, ({ id, input }: { id: number; input: MiloCustomerInput }) => setMiloCustomer(id, input));

export const useSetMiloCustomer = () => useMutation(setMiloCustomerOptions(useQueryClient()));

/** A key check changes nothing, so nothing refetches. */
export const useTestMiloKey = () => useMutation({ mutationFn: (provider: MiloProvider) => testMiloKey(provider) });
